/**
 * feat-284 — the language-mode model behind `/quiz-review`.
 *
 * Pure functions only: URL ⇄ mode, mode → API parameter, and per-field
 * resolution of the additive translation overlay. Nothing here imports React,
 * so all of it is directly unit-testable without a render.
 */

import type {
  TranslationOverlay,
  TranslatedField,
} from '../../services/quizContentAdminService';

/**
 * The three states of the control.
 *
 * `alt` rather than `es` on purpose: the non-English language is a VARIABLE
 * (§5 — a tenant may configure more than one), and a mode named `es` invites
 * exactly the hard-coding this feature is meant to avoid. The selected code
 * lives beside the mode, in {@link LanguageSelection}.
 */
export type LanguageMode = 'en' | 'alt' | 'both';

export interface LanguageSelection {
  mode: LanguageMode;
  /**
   * The selected non-English language code (`es`), or `''` when the tenant has
   * none configured. Meaningful in EVERY mode, including `en` — it is what the
   * page has already fetched, which is what makes EN→ALT→BOTH free of network
   * traffic (§3a "fetch once, toggle free", AC4).
   */
  language: string;
}

/** EN → ALT → BOTH → EN. Forward only; there is no reverse cycle (spec §Assumptions 3). */
export const MODE_CYCLE: readonly LanguageMode[] = ['en', 'alt', 'both'];

export function nextMode(mode: LanguageMode): LanguageMode {
  const i = MODE_CYCLE.indexOf(mode);
  return MODE_CYCLE[(i + 1) % MODE_CYCLE.length];
}

/** The query-string key. `?lang=` per spec §Assumptions 2. */
export const LANG_PARAM = 'lang';

function normalizeToken(token: string): string {
  return token.trim().toLowerCase();
}

/**
 * Read the browser URL's `?lang=` into a mode + language.
 *
 * `available` is the tenant's configured non-English languages (`en`-free). A
 * value naming a language the tenant does not have is NOT honoured — it falls
 * back to EN, the same as garbage, because rendering "Spanish" for a tenant
 * that has no Spanish would be an empty promise the overlay cannot keep.
 *
 * Never throws. Spec §2: "An unrecognized `?lang=` value falls back to EN".
 */
export function parseLangParam(raw: string | null, available: string[]): LanguageSelection {
  const langs = available.map(normalizeToken).filter((c) => c && c !== 'en');
  const fallback: LanguageSelection = { mode: 'en', language: langs[0] ?? '' };
  if (langs.length === 0) return { mode: 'en', language: '' };
  if (!raw) return fallback;

  const tokens = raw.split(',').map(normalizeToken).filter(Boolean);
  if (tokens.length === 0) return fallback;

  const nonEnglish = tokens.filter((t) => t !== 'en');
  const hasEnglish = tokens.some((t) => t === 'en');

  // `en` alone (or repeated) — explicit English.
  if (nonEnglish.length === 0) return fallback;
  // More than one non-English language is not a thing: Both is ALWAYS English
  // plus exactly one other (spec §Assumptions 6 — two columns, never three).
  if (nonEnglish.length > 1) return fallback;

  const picked = nonEnglish[0];
  if (!langs.includes(picked)) return fallback;

  return { mode: hasEnglish ? 'both' : 'alt', language: picked };
}

/**
 * The value to write back into the browser URL, or `null` to drop the parameter.
 *
 * EN drops it rather than writing `lang=en` so that a reviewer who never touches
 * the control ends up with the URL they started with (spec §Assumptions 1).
 */
export function langParamFor(selection: LanguageSelection): string | null {
  if (selection.mode === 'en' || !selection.language) return null;
  return selection.mode === 'both' ? `en,${selection.language}` : selection.language;
}

/**
 * ⚠️ THE API PARAMETER IS NOT THE URL PARAMETER. Read `FRONTEND-SLICE.md` §3a.
 *
 * `normalize_lang('en,es') === 'en'` server-side, so handing the browser's
 * Both-mode `?lang=` value to the API asks for English, receives no overlay, and
 * renders two identical English columns — silently, with no 4xx and nothing in
 * the console. ALT and BOTH therefore make the **identical** request; they
 * differ only in how the single payload already in hand is rendered.
 *
 * EN also requests the overlay. That is deliberate and is what AC4 measures: if
 * the first load skipped it, the reviewer's first click on ES would have to go
 * to the network, and "clicking EN → ES → Both fires no additional network
 * request" would be unsatisfiable. The overlay is additive, so carrying it in EN
 * mode changes nothing that renders.
 */
export function apiLangFor(selection: LanguageSelection): string | undefined {
  return selection.language || undefined;
}

/** What, if anything, a resolved field is marked with. */
export type FieldMarker = 'none' | 'untranslated' | 'stale';

export interface ResolvedField {
  /** UNTRUSTED when `lang` is non-null — render as a JSX text node only. */
  text: string;
  marker: FieldMarker;
  /**
   * The BCP-47 code `text` is written in, or `null` for canonical English.
   *
   * Consumed as the `lang` attribute on the element carrying the run. Not
   * cosmetic: with the document at `lang="en"` this is what drives screen-reader
   * voice selection and hyphenation, and in Both mode it is the ONLY thing
   * distinguishing the two columns to a non-visual reader (design notes §2).
   */
  lang: string | null;
}

/**
 * Resolve one translatable field for one column.
 *
 * `language === null` means the ENGLISH column (or EN mode): canonical text,
 * never a marker, whatever the overlay says. Spec: markers appear in ES mode and
 * the Spanish column of Both, never in EN mode or the English column.
 *
 * Otherwise:
 *  - overlay field key ABSENT ⇒ untranslated ⇒ canonical English, marked `EN`.
 *    Absent, not `null` and not `''` — do not "helpfully" coalesce here.
 *  - present and `stale` ⇒ the translation, marked `Outdated`.
 *  - present and fresh ⇒ the translation, unmarked.
 *
 * An empty canonical with no translation is never marked: there is no text for
 * the chip to qualify, and the page does not render the line at all.
 */
export function resolveField<TField extends string>(
  canonical: string | null | undefined,
  overlay: TranslationOverlay<TField> | undefined,
  language: string | null,
  field: TField,
): ResolvedField {
  const english = canonical ?? '';
  if (!language) return { text: english, marker: 'none', lang: null };

  const entry: TranslatedField | undefined = overlay?.[language]?.[field];
  if (entry === undefined) {
    return { text: english, marker: english ? 'untranslated' : 'none', lang: null };
  }
  return { text: entry.text, marker: entry.stale ? 'stale' : 'none', lang: language };
}
